import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { selectTimeOption } from '../redux/slices/bookingSlice';
import RegistrationStepperHeader from '../components/RegistrationStepperHeader';
import StepperButtons from '../components/StepperButtons';
import Swal from 'sweetalert2';

const TimeSlotSelection = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { eventDetails, selectedService, selectedTimeOption, totalPrice } = useSelector((state) => state.booking);

  useEffect(() => {
    if (!selectedService) {
      navigate('/service');
      return;
    }
    
    // Auto-skip if no time slots available
    const timeOptions = eventDetails?.timeOptions || [];
    
    if (timeOptions.length === 0) {
      // No time slots, go directly to devotee
      navigate('/devotee');
    }
  }, [selectedService, eventDetails, navigate]);

  const handleTimeSelect = (timeOption) => {
    dispatch(selectTimeOption(timeOption));
  };

  const handleNext = () => {
    if (!selectedTimeOption) {
      Swal.fire({
        title: 'Time Slot Required',
        text: 'Please select a time slot before continuing',
        icon: 'warning',
        confirmButtonText: 'OK',
        confirmButtonColor: '#3b82f6'
      });
      return;
    }
    navigate('/devotee');
  };

  const timeOptions = eventDetails?.timeOptions || [];
  const optionalServices = eventDetails?.optionalServices || [];
  const backPath = optionalServices.length > 0 ? '/addon' : '/service';

  // Sort time options by display_Order or event_Time_Option_Id
  const sortedTimeOptions = [...timeOptions].sort((a, b) => {
    return (a.display_Order || a.event_Time_Option_Id) - (b.display_Order || b.event_Time_Option_Id);
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 pb-24">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-5xl mx-auto">
          <div className="overflow-hidden rounded-[28px] bg-white/70 backdrop-blur-xl shadow-[0_26px_70px_-45px_rgba(15,23,42,0.55)] border border-white/60 mb-4">
            <RegistrationStepperHeader
              title={(eventDetails?.event?.event_Name || eventDetails?.Event?.event_Name || 'Event Registration')}
              subtitle={null}
              backPath={backPath}
              backLabel="Back"
              eventDetails={eventDetails}
            />

            <div className="p-6">
              <div className="rounded-2xl bg-white/85 border border-gray-200/70 shadow-sm">
                <div className="px-5 pt-4 pb-3 border-b border-gray-200/70">
                  <div className="text-sm font-semibold text-gray-900">Timing Selection</div>
                  <div className="text-xs text-gray-500">Select a time slot.</div>
                </div>

                <div className="p-5">
                  {sortedTimeOptions.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {sortedTimeOptions.map((timeOption) => {
                        const isSelected = selectedTimeOption?.event_Time_Option_Id === timeOption.event_Time_Option_Id;
                        return (
                          <div
                            key={timeOption.event_Time_Option_Id}
                            onClick={() => handleTimeSelect(timeOption)}
                            className={`relative cursor-pointer rounded-2xl border transition-all overflow-hidden ${
                              isSelected
                                ? 'border-indigo-500 shadow-[0_18px_45px_-28px_rgba(79,70,229,0.55)] bg-gradient-to-b from-indigo-50/60 via-white to-white'
                                : 'border-gray-200 bg-white hover:shadow-md'
                            }`}
                          >
                            <div className="absolute inset-0 pointer-events-none">
                              <div className="absolute -left-10 -top-10 h-28 w-28 rounded-full bg-indigo-500/10 blur-2xl"></div>
                              <div className="absolute right-0 top-0 h-28 w-28 rounded-full bg-amber-400/10 blur-2xl"></div>
                            </div>

                            <div className="relative p-4">
                              <div className="flex items-start justify-between gap-3">
                                <div className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-sm border border-gray-200">
                                  <i className="fas fa-clock text-gray-700"></i>
                                </div>

                                <div className={`shrink-0 inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold border shadow-sm ${
                                  isSelected
                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                    : 'bg-white text-gray-600 border-gray-200'
                                }`}>
                                  {isSelected ? 'Active' : ' '}
                                </div>
                              </div>

                              <div className="mt-3 text-sm font-semibold text-gray-900">
                                {timeOption.time_Option_Name}
                              </div>

                              {(timeOption.start_Time && timeOption.end_Time) ? (
                                <div className="mt-1 text-xs text-gray-600">
                                  {timeOption.start_Time} - {timeOption.end_Time}
                                </div>
                              ) : null}

                              {timeOption.time_Option_Description ? (
                                <div className="mt-1 text-xs text-gray-500 line-clamp-2">
                                  {timeOption.time_Option_Description}
                                </div>
                              ) : null}

                              <div className="mt-4 flex items-center justify-between gap-3">
                                <div
                                  className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-semibold border ${
                                    isSelected
                                      ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                                      : 'bg-white text-gray-500 border-gray-200'
                                  }`}
                                >
                                  {isSelected ? (
                                    <>
                                      <i className="fas fa-check text-[10px]"></i>
                                      Selected
                                    </>
                                  ) : (
                                    <>Tap to select</>
                                  )}
                                </div>

                                {isSelected ? (
                                  <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-semibold text-emerald-700 border border-emerald-200">
                                    <i className="fas fa-check-circle text-[10px]"></i>
                                    Active
                                  </div>
                                ) : (
                                  <div className="inline-flex items-center gap-2 rounded-full bg-gray-50 px-3 py-1 text-[11px] font-semibold text-gray-500 border border-gray-200">
                                    <i className="fas fa-circle text-[8px]"></i>
                                    Inactive
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="relative h-1.5 bg-slate-100">
                              <div
                                className={`h-full transition-all ${
                                  isSelected
                                    ? 'w-full bg-gradient-to-r from-indigo-600 via-purple-600 to-amber-500'
                                    : 'w-0 bg-transparent'
                                }`}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-200">
                      <i className="fas fa-inbox text-gray-400 text-6xl mb-4"></i>
                      <p className="text-gray-600 text-lg">No time slots available for this event</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <StepperButtons
        wrapperClassName="bg-white/90 backdrop-blur-xl"
        containerClassName="px-4 py-3"
        backPath={backPath}
        onNext={handleNext}
        nextLabel="Next"
        nextDisabled={!selectedTimeOption}
        centerContent={
          <div className="text-xs text-gray-500">
            Total:
            <span className="ml-2 text-sm font-semibold text-gray-900">
              ${Number(totalPrice || 0).toFixed(2)}
            </span>
          </div>
        }
      />
    </div>
  );
};

export default TimeSlotSelection;
